// No media URL is assigned until window.load and proximity to the section.
const pageLoaded = new Promise(resolve => {
  if (document.readyState === 'complete') resolve();
  else window.addEventListener('load', resolve, { once: true });
});

// Respect visitors who ask for less motion or less data: show a play button instead of autoplaying.
const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const connection = navigator.connection;
const saveData = Boolean(connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || '')));

function initialize(scope = document) {
  for (const video of scope.querySelectorAll('[data-deferred-video]')) {
    if (video.dataset.deferredReady) continue;
    video.dataset.deferredReady = 'true';
    const panel = video.parentElement.querySelector('[data-video-loading]');
    const label = panel.querySelector('[data-video-loading-label]');
    const spinner = panel.querySelector('.video-loading-spinner');
    const retry = panel.querySelector('[data-video-retry]');
    let started = false;
    let userPaused = false;
    let timer;

    function loading() {
      panel.hidden = false;
      spinner.hidden = false;
      retry.hidden = true;
      label.textContent = 'Loading video…';
      clearTimeout(timer);
      timer = setTimeout(() => failed('Video is taking longer to load.'), 25000);
    }
    function failed(message) {
      clearTimeout(timer);
      panel.hidden = false;
      spinner.hidden = true;
      retry.hidden = false;
      label.textContent = message;
    }
    function play() {
      video.play()?.catch(() => failed('Video paused.'));
    }
    function start() {
      if (started || !video.isConnected) return;
      started = true;
      loading();
      video.muted = true;
      for (const source of video.querySelectorAll('source[data-src]')) {
        source.src = source.dataset.src;
        source.addEventListener('error', () => {
          source.dataset.failed = 'true';
          if ([...video.querySelectorAll('source')].every(item => item.dataset.failed)) {
            failed('Video unavailable.');
          }
        });
      }
      video.preload = 'auto';
      video.load();
      play();
    }
    video.addEventListener('playing', () => {
      clearTimeout(timer);
      panel.hidden = true;
    });
    video.addEventListener('waiting', () => { if (started) loading(); });
    video.addEventListener('error', () => failed('Video unavailable.'));
    retry.addEventListener('click', () => {
      if (!started) return start();
      userPaused = false;
      loading();
      if (video.error || video.readyState === 0) {
        for (const source of video.querySelectorAll('source')) delete source.dataset.failed;
        video.load();
      }
      play();
    });

    // The video is position:fixed inside a parallax scene, so its own box is always "on screen".
    // Watch the scrolling section container instead, for both proximity and pause/resume.
    const visibilityTarget = video.closest('[data-video-scroll-scene], [data-video-parallax]') || video.parentElement || video;

    pageLoaded.then(() => {
      if (!video.isConnected) return;
      if (prefersReducedMotion || saveData) {
        // Never download or autoplay; the visitor can start it.
        panel.hidden = false;
        spinner.hidden = true;
        retry.hidden = false;
        label.textContent = prefersReducedMotion ? 'Video paused (reduced motion).' : 'Video paused to save data.';
        return;
      }
      if ('IntersectionObserver' in window) {
        // Pause while the section is off-screen to save battery and CPU; resume when it returns.
        new IntersectionObserver(entries => {
          if (!started) return;
          const visible = entries.some(entry => entry.isIntersecting);
          if (!visible && !video.paused) video.pause();
          else if (visible && video.paused && !userPaused) video.play()?.catch(() => {});
        }).observe(visibilityTarget);
      }
      const schedule = () => {
        if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 1000 });
        else setTimeout(start, 0);
      };
      if (!('IntersectionObserver' in window)) return schedule();
      const observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          observer.disconnect();
          schedule();
        }
      }, { rootMargin: '300px' });
      observer.observe(visibilityTarget);
      document.addEventListener('shopify:section:unload', event => {
        if (event.target.contains(video)) {
          observer.disconnect();
          clearTimeout(timer);
        }
      });
    });
  }
}

initialize();
document.addEventListener('shopify:section:load', event => initialize(event.target));
